// Turns a clicked DOM element into a compact, human/AI-readable text block that
// tells Claude exactly where on the page a bug was reported. Injected into the
// "Report a Bug" Description so the raised task carries a precise location.

const escapeId = (value) => {
  if (typeof window !== "undefined" && window.CSS && window.CSS.escape) {
    return window.CSS.escape(value);
  }
  // Minimal fallback for environments without CSS.escape.
  return String(value).replace(/([^a-zA-Z0-9_-])/g, "\\$1");
};

// Build a reasonably unique CSS selector by walking up the tree. Stops early at
// the first ancestor carrying an id (ids are unique enough to anchor a path).
const cssPath = (el) => {
  if (!el || el.nodeType !== 1) return "";

  const parts = [];
  let node = el;

  while (node && node.nodeType === 1 && parts.length < 8) {
    if (node.id) {
      parts.unshift(`#${escapeId(node.id)}`);
      break;
    }

    const current = node;
    let selector = current.nodeName.toLowerCase();
    const parent = current.parentNode;

    if (parent) {
      const sameTag = Array.from(parent.children).filter(
        (child) => child.nodeName === current.nodeName
      );
      if (sameTag.length > 1) {
        selector += `:nth-of-type(${sameTag.indexOf(current) + 1})`;
      }
    }

    parts.unshift(selector);
    node = parent;
  }

  return parts.join(" > ");
};

const truncate = (value, max = 120) => {
  const clean = (value || "").replace(/\s+/g, " ").trim();
  return clean.length > max ? `${clean.slice(0, max)}…` : clean;
};

// Nearest ancestor (including self) that carries a stable hook Claude can grep.
const nearestTestAnchor = (el) => {
  let node = el;
  while (node && node.nodeType === 1) {
    const testId =
      node.getAttribute("data-testid") ||
      node.getAttribute("data-test") ||
      node.getAttribute("data-cy");
    if (testId) return `[data-testid="${testId}"]`;
    if (node.id) return `#${node.id}`;
    node = node.parentElement;
  }
  return "";
};

export const describeElement = (el) => {
  if (!el || el.nodeType !== 1) return "";

  const rect = el.getBoundingClientRect();
  const attrs = [];

  const pushAttr = (name, value) => {
    if (value) attrs.push(`${name}="${value}"`);
  };

  pushAttr("id", el.id);
  pushAttr("name", el.getAttribute("name"));
  pushAttr("type", el.getAttribute("type"));
  pushAttr("role", el.getAttribute("role"));
  pushAttr("aria-label", el.getAttribute("aria-label"));
  pushAttr("placeholder", el.getAttribute("placeholder"));
  pushAttr("title", el.getAttribute("title"));
  pushAttr("href", el.getAttribute("href"));
  pushAttr(
    "data-testid",
    el.getAttribute("data-testid") ||
      el.getAttribute("data-test") ||
      el.getAttribute("data-cy")
  );

  const className =
    typeof el.className === "string" ? el.className.trim() : "";
  const anchor = nearestTestAnchor(el);

  const lines = [
    "----- Reported element (auto-captured) -----",
    `Selector: ${cssPath(el)}`,
    `Tag: <${el.tagName.toLowerCase()}>`,
    className ? `Class: ${truncate(className, 200)}` : null,
    attrs.length ? `Attributes: ${attrs.join("  ")}` : null,
    anchor ? `Nearest anchor: ${anchor}` : null,
    el.textContent && truncate(el.textContent)
      ? `Text: "${truncate(el.textContent)}"`
      : null,
    `Position: x=${Math.round(rect.left)} y=${Math.round(
      rect.top
    )} w=${Math.round(rect.width)} h=${Math.round(rect.height)} (viewport px)`,
    typeof window !== "undefined" ? `URL: ${window.location.href}` : null,
    "--------------------------------------------",
    "",
  ].filter(Boolean);

  return lines.join("\n");
};

export default describeElement;
